import React, { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Packaging calculation logic with full null safety
const calculatePackaging = (cartItems) => {
  const packaging = [];
  
  // Safety check: ensure cartItems is a valid array
  if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) {
    return packaging;
  }
  
  // Count items by category
  let hamburgerCount = 0;
  let langosCount = 0;
  let paperPackagingCount = 0;

  cartItems.forEach(item => {
    // Skip if item is null/undefined
    if (!item) return;
    
    const category = (item.category || '').toLowerCase();
    const productId = (item.productId || '').toLowerCase();
    const quantity = item.quantity || 1;

    // Hamburgers - 1 box per hamburger
    if (category.includes('hamburger') || productId.includes('burger')) {
      hamburgerCount += quantity;
    }
    // Lángós (including lactose-free) - 1 box per lángos
    else if (category.includes('lángós') || category.includes('langos') || productId.includes('langos')) {
      langosCount += quantity;
    }
    // Paper packaging for hot dogs, fries, gofri, sauces
    else if (
      category.includes('hot-dog') || 
      productId.includes('hotdog') ||
      category.includes('köret') || 
      productId.includes('side') ||
      productId.includes('fries') ||
      category.includes('gofri') || 
      productId.includes('waffle') ||
      category.includes('szósz') || 
      category.includes('öntet') ||
      productId.includes('sauce')
    ) {
      paperPackagingCount += quantity;
    }
  });

  // Add packaging items only if count > 0
  if (hamburgerCount > 0) {
    packaging.push({
      id: 'pkg-hamburger-box',
      name: 'Ételes doboz',
      price: 150,
      quantity: hamburgerCount,
      category: 'Csomagolás',
      productId: 'packaging-burger-box',
      isPackaging: true
    });
  }

  if (langosCount > 0) {
    packaging.push({
      id: 'pkg-langos-box',
      name: 'Lángos doboz',
      price: 250,
      quantity: langosCount,
      category: 'Csomagolás',
      productId: 'packaging-langos-box',
      isPackaging: true
    });
  }

  if (paperPackagingCount > 0) {
    packaging.push({
      id: 'pkg-paper',
      name: 'Ételes doboz',
      price: 150,
      quantity: paperPackagingCount,
      category: 'Csomagolás',
      productId: 'packaging-paper',
      isPackaging: true
    });
  }

  return packaging;
};

// Helper function to load cart from localStorage
const loadCartFromStorage = () => {
  try {
    const savedCart = localStorage.getItem('boldogBufeCart');
    if (savedCart) {
      const parsed = JSON.parse(savedCart);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (error) {
    console.error('Error loading cart from localStorage:', error);
  }
  return [];
};

export const CartProvider = ({ children }) => {
  // Initialize state directly from localStorage to avoid race condition
  const [cartItems, setCartItems] = useState(() => loadCartFromStorage());
  const [packagingItems, setPackagingItems] = useState(() => calculatePackaging(loadCartFromStorage()));
  const [isInitialized, setIsInitialized] = useState(false);

  // Mark as initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Save cart to localStorage and recalculate packaging whenever cart changes
  // Only save after initialization to avoid overwriting existing data
  useEffect(() => {
    if (!isInitialized) return;
    
    try {
      localStorage.setItem('boldogBufeCart', JSON.stringify(cartItems));
      // Recalculate packaging with null safety
      const newPackaging = calculatePackaging(cartItems);
      setPackagingItems(newPackaging || []);
    } catch (error) {
      console.error('Error saving cart or calculating packaging:', error);
      setPackagingItems([]);
    }
  }, [cartItems, isInitialized]);

  const addToCart = (item) => {
    if (!item) return;
    
    // Generate unique cartItemId for each addition (even if same product)
    const cartItemId = `${item.id}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    setCartItems(prevItems => {
      // NO quantity merging! Every add is a separate item
      return [...prevItems, { ...item, cartItemId, quantity: 1 }];
    });
  };

  const removeFromCart = (cartItemId) => {
    setCartItems(prevItems => prevItems.filter(item => item && item.cartItemId !== cartItemId));
  };

  const updateQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartItemId);
      return;
    }
    setCartItems(prevItems =>
      prevItems.map(item =>
        item && item.cartItemId === cartItemId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setPackagingItems([]);
    try {
      localStorage.removeItem('boldogBufeCart');
    } catch (error) {
      console.error('Error clearing cart from localStorage:', error);
    }
  };

  const getCartTotal = () => {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((total, item) => {
      if (!item) return total;
      const price = item.price || 0;
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  const getPackagingTotal = () => {
    if (!Array.isArray(packagingItems)) return 0;
    return packagingItems.reduce((total, item) => {
      if (!item) return total;
      const price = item.price || 0;
      const quantity = item.quantity || 0;
      return total + (price * quantity);
    }, 0);
  };

  const getGrandTotal = () => {
    return getCartTotal() + getPackagingTotal();
  };

  const getCartItemsCount = () => {
    if (!Array.isArray(cartItems)) return 0;
    return cartItems.reduce((count, item) => {
      if (!item) return count;
      return count + (item.quantity || 0);
    }, 0);
  };

  const getAllItems = () => {
    const items = Array.isArray(cartItems) ? cartItems : [];
    const packaging = Array.isArray(packagingItems) ? packagingItems : [];
    return [...items, ...packaging];
  };

  return (
    <CartContext.Provider
      value={{
        cartItems: cartItems || [],
        packagingItems: packagingItems || [],
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getPackagingTotal,
        getGrandTotal,
        getCartItemsCount,
        getAllItems
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
